// src/services/advancedAttendanceService.ts
import {
  collection,
  collectionGroup,
  doc,
  documentId,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  runTransaction,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import { tenantCollection, tenantDocument } from './tenantService';
// import { notificationService } from './notificationService';

export interface AttendanceRecord {
  id: string;
  studentId: string;
  studentName: string;
  sessionId: string;
  sessionTitle: string;
  date: Date;
  status: 'present' | 'absent' | 'late' | 'excused';
  checkInTime?: Date;
  lateMinutes?: number;
  notes?: string;
  zoomJoinTime?: Date;
  zoomLeaveTime?: Date;
  zoomDuration?: number; // بالدقائق
  autoMarked: boolean; // هل تم تسجيل الحضور تلقائياً من Zoom
}

export interface Session {
  id: string;
  title: string;
  date: Date;
  startTime: Date;
  endTime: Date;
  duration: number;
  zoomMeetingId?: string;
  zoomPassword?: string;
  teacherId: string;
  studentsIds: string[];
  studentsCount: number;
  attendedCount: number;
  status: 'scheduled' | 'ongoing' | 'completed' | 'cancelled';
  autoAttendanceEnabled: boolean;
  reminderSent: boolean;
}

export interface AttendanceSettings {
  teacherId: string;
  autoAttendanceEnabled: boolean;
  reminderSettings: {
    enabled: boolean;
    minutesBefore: number;
    includeParents: boolean;
    includeWhatsapp: boolean;
  };
  lateThresholdMinutes: number; // عدد الدقائق المسموحة قبل اعتبار الطالب متأخر
  earlyLeaveThresholdMinutes: number; // عدد الدقائق قبل انتهاء الحصة للسماح بالمغادرة
}

export interface ZoomWebhookData {
  event:
    | 'meeting.participant_joined'
    | 'meeting.participant_left'
    | 'meeting.started'
    | 'meeting.ended';
  payload: {
    object: {
      id: string; // Meeting ID
      participant?: {
        id: string;
        user_name: string;
        email?: string;
        join_time: string;
        leave_time?: string;
      };
    };
  };
}

class AdvancedAttendanceService {
  private readonly sessionsCollection = 'sessions';
  private readonly attendanceCollection = 'attendance';
  private readonly settingsCollection = 'attendanceSettings';

  // إعدادات المعلم
  async getTeacherAttendanceSettings(
    teacherId: string
  ): Promise<AttendanceSettings | null> {
    try {
      const settingsDoc = await getDoc(
        doc(db, this.settingsCollection, teacherId)
      );
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        return {
          ...data,
        } as AttendanceSettings;
      }

      // إعدادات افتراضية
      return {
        teacherId,
        autoAttendanceEnabled: true,
        reminderSettings: {
          enabled: true,
          minutesBefore: 15,
          includeParents: true,
          includeWhatsapp: false,
        },
        lateThresholdMinutes: 10,
        earlyLeaveThresholdMinutes: 15,
      };
    } catch (error) {
      console.error('Error fetching attendance settings:', error);
      return null;
    }
  }

  async updateTeacherAttendanceSettings(
    settings: AttendanceSettings
  ): Promise<void> {
    try {
      await setDoc(doc(db, this.settingsCollection, settings.teacherId), {
        ...settings,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating attendance settings:', error);
      throw error;
    }
  }

  // إدارة الجلسات
  async getTeacherSessions(
    teacherId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<Session[]> {
    try {
      let tenantSessionsQuery = query(
        tenantCollection(teacherId, 'sessions'),
        where('teacherId', '==', teacherId),
        orderBy('date', 'desc')
      );

      if (startDate && endDate) {
        tenantSessionsQuery = query(
          tenantSessionsQuery,
          where('date', '>=', startDate),
          where('date', '<=', endDate)
        );
      }

      const tenantSnapshot = await getDocs(tenantSessionsQuery);
      const mapSession = (docSnapshot: any) => {
        const data = docSnapshot.data();
        return {
          id: docSnapshot.id,
          ...data,
          studentsIds: data.studentsIds || data.studentIds || [],
          date: data.date?.toDate(),
          startTime: data.startTime?.toDate(),
          endTime: data.endTime?.toDate(),
        } as Session;
      };
      return tenantSnapshot.docs.map(mapSession);
    } catch (error) {
      console.error('Error fetching teacher sessions:', error);
      return [];
    }
  }

  async createSession(sessionData: Omit<Session, 'id'>): Promise<string> {
    try {
      const sessionRef = doc(
        tenantCollection(sessionData.teacherId, 'sessions')
      );
      await setDoc(sessionRef, {
        ...sessionData,
        tenantId: sessionData.teacherId,
        date: sessionData.date,
        startTime: sessionData.startTime,
        endTime: sessionData.endTime,
        createdAt: serverTimestamp(),
      });
      return sessionRef.id;
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  }

  async updateSessionStatus(
    sessionId: string,
    status: Session['status']
  ): Promise<void> {
    try {
      const session = await this.getSession(sessionId);
      if (!session?.teacherId) {
        throw new Error('Session teacher ownership is required');
      }
      const tenantRef = tenantDocument(
        session.teacherId,
        'sessions',
        sessionId
      );
      const tenantSnapshot = await getDoc(tenantRef);
      if (!tenantSnapshot.exists()) {
        throw new Error('Session not found in tenant scope');
      }
      await updateDoc(tenantRef, {
        status,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating session status:', error);
      throw error;
    }
  }

  // إدارة الحضور
  async getSessionAttendance(sessionId: string): Promise<AttendanceRecord[]> {
    try {
      const session = await this.getSession(sessionId);
      if (!session?.teacherId) return [];
      const tenantAttendanceQuery = query(
        tenantCollection(session.teacherId, 'attendance'),
        where('sessionId', '==', sessionId),
        orderBy('studentName')
      );

      const tenantSnapshot = await getDocs(tenantAttendanceQuery);
      const mapAttendance = (docSnapshot: any) => {
        const data = docSnapshot.data();
        return {
          id: docSnapshot.id,
          ...data,
          date: data.date?.toDate(),
          checkInTime: data.checkInTime?.toDate(),
          zoomJoinTime: data.zoomJoinTime?.toDate(),
          zoomLeaveTime: data.zoomLeaveTime?.toDate(),
        } as AttendanceRecord;
      };
      return tenantSnapshot.docs.map(mapAttendance);
    } catch (error) {
      console.error('Error fetching session attendance:', error);
      return [];
    }
  }

  async markAttendance(
    studentId: string,
    sessionId: string,
    status: AttendanceRecord['status'],
    notes?: string,
    autoMarked: boolean = false
  ): Promise<void> {
    try {
      const session = await this.getSession(sessionId);

      if (!session) {
        throw new Error('Session not found');
      }
      const attendanceRef = tenantDocument(
        session.teacherId,
        'attendance',
        `${sessionId}_${studentId}`
      );
      const tenantSessionRef = tenantDocument(
        session.teacherId,
        'sessions',
        sessionId
      );
      const tenantSessionSnapshot = await getDoc(tenantSessionRef);
      if (!tenantSessionSnapshot.exists()) {
        throw new Error('Session not found in tenant scope');
      }

      await runTransaction(db, async transaction => {
        // تحديث سجل الحضور
        transaction.set(
          attendanceRef,
          {
            studentId,
            teacherId: session.teacherId,
            tenantId: session.teacherId,
            sessionId,
            status,
            notes,
            autoMarked,
            checkInTime: status !== 'absent' ? new Date() : null,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        // تحديث عداد الحضور في الجلسة
        const attendanceRecords = await this.getSessionAttendance(sessionId);
        const attendedCount = attendanceRecords.filter(
          r => r.status === 'present' || r.status === 'late'
        ).length;

        transaction.update(tenantSessionRef, {
          attendedCount:
            attendedCount + (status === 'present' || status === 'late' ? 1 : 0),
        });
      });
    } catch (error) {
      console.error('Error marking attendance:', error);
      throw error;
    }
  }

  private async getSession(sessionId: string): Promise<Session | null> {
    try {
      const tenantSessionSnapshot = await getDocs(
        query(
          collectionGroup(db, 'sessions'),
          where(documentId(), '==', sessionId),
          limit(1)
        )
      );
      if (!tenantSessionSnapshot.empty) {
        const sessionDoc = tenantSessionSnapshot.docs[0];
        const data = sessionDoc.data();
        return {
          id: sessionDoc.id,
          ...data,
          studentsIds: data.studentsIds || data.studentIds || [],
          date: data.date?.toDate(),
          startTime: data.startTime?.toDate(),
          endTime: data.endTime?.toDate(),
        } as Session;
      }
      return null;
    } catch (error) {
      console.error('Error fetching session:', error);
      return null;
    }
  }

  // معالجة إشعارات Zoom
  async processZoomWebhook(webhookData: ZoomWebhookData): Promise<void> {
    try {
      const { event, payload } = webhookData;
      const meetingId = payload.object.id;

      // العثور على الجلسة المرتبطة بـ Meeting ID
      const session = await this.findSessionByZoomMeetingId(meetingId);
      if (!session || !session.autoAttendanceEnabled) {
        return;
      }

      switch (event) {
        case 'meeting.participant_joined':
          await this.handleParticipantJoined(
            session,
            payload.object.participant!
          );
          break;

        case 'meeting.participant_left':
          await this.handleParticipantLeft(
            session,
            payload.object.participant!
          );
          break;

        case 'meeting.started':
          await this.updateSessionStatus(session.id, 'ongoing');
          break;

        case 'meeting.ended':
          await this.updateSessionStatus(session.id, 'completed');
          break;
      }
    } catch (error) {
      console.error('Error processing Zoom webhook:', error);
    }
  }

  private async findSessionByZoomMeetingId(
    meetingId: string
  ): Promise<Session | null> {
    try {
      const q = query(
        collectionGroup(db, 'sessions'),
        where('zoomMeetingId', '==', meetingId),
        limit(1)
      );

      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          date: data.date?.toDate(),
          startTime: data.startTime?.toDate(),
          endTime: data.endTime?.toDate(),
        } as Session;
      }
      return null;
    } catch (error) {
      console.error('Error finding session by Zoom meeting ID:', error);
      return null;
    }
  }

  private async handleParticipantJoined(
    session: Session,
    participant: any
  ): Promise<void> {
    try {
      // العثور على الطالب بناءً على البريد الإلكتروني أو الاسم
      const student = await this.findStudentByZoomInfo(
        session.teacherId,
        participant.email,
        participant.user_name,
        session.studentsIds
      );

      if (!student) {
        console.log(
          `Student not found for Zoom participant: ${participant.user_name}`
        );
        return;
      }

      const joinTime = new Date(participant.join_time);
      const sessionStartTime = session.startTime;
      const settings = await this.getTeacherAttendanceSettings(
        session.teacherId
      );

      // تحديد حالة الحضور
      let status: AttendanceRecord['status'] = 'present';
      let lateMinutes = 0;

      if (joinTime > sessionStartTime) {
        const diffMinutes = Math.floor(
          (joinTime.getTime() - sessionStartTime.getTime()) / (1000 * 60)
        );
        if (diffMinutes > (settings?.lateThresholdMinutes || 10)) {
          status = 'late';
          lateMinutes = diffMinutes;
        }
      }

      // تسجيل الحضور
      await this.markAttendanceWithZoomData(
        session.teacherId,
        student.id,
        session.id,
        status,
        joinTime,
        lateMinutes,
        true
      );

      console.log(
        `Auto-marked attendance for student ${student.name}: ${status}`
      );
    } catch (error) {
      console.error('Error handling participant joined:', error);
    }
  }

  private async handleParticipantLeft(
    session: Session,
    participant: any
  ): Promise<void> {
    try {
      const student = await this.findStudentByZoomInfo(
        session.teacherId,
        participant.email,
        participant.user_name,
        session.studentsIds
      );

      if (!student) return;

      const leaveTime = new Date(participant.leave_time);

      // تحديث وقت المغادرة ومدة الحضور
      await this.updateAttendanceZoomLeaveTime(
        session.teacherId,
        student.id,
        session.id,
        leaveTime
      );
    } catch (error) {
      console.error('Error handling participant left:', error);
    }
  }

  private async findStudentByZoomInfo(
    teacherId: string,
    email?: string,
    userName?: string,
    sessionStudentIds: string[] = []
  ): Promise<{ id: string; name: string } | null> {
    try {
      // البحث في مجموعة الطلاب
      // هذا مثال مبسط - في الواقع ستحتاج لتنفيذ البحث في مجموعة الطلاب

      // البحث بالبريد الإلكتروني أولاً
      if (email) {
        const q = query(
          tenantCollection(teacherId, 'students'),
          where('email', '==', email),
          where('id', 'in', sessionStudentIds.slice(0, 10)) // Firestore limits 'in' queries to 10
        );

        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          const data = doc.data();
          return { id: doc.id, name: data.name };
        }
      }

      // البحث بالاسم كخيار احتياطي
      if (userName) {
        const q = query(
          tenantCollection(teacherId, 'students'),
          where('name', '==', userName),
          where('id', 'in', sessionStudentIds.slice(0, 10))
        );

        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          const data = doc.data();
          return { id: doc.id, name: data.name };
        }
      }

      return null;
    } catch (error) {
      console.error('Error finding student by Zoom info:', error);
      return null;
    }
  }

  private async markAttendanceWithZoomData(
    teacherId: string,
    studentId: string,
    sessionId: string,
    status: AttendanceRecord['status'],
    zoomJoinTime: Date,
    lateMinutes: number,
    autoMarked: boolean
  ): Promise<void> {
    try {
      const attendanceRef = tenantDocument(
        teacherId,
        'attendance',
        `${sessionId}_${studentId}`
      );

      await setDoc(
        attendanceRef,
        {
          studentId,
          teacherId,
          tenantId: teacherId,
          sessionId,
          status,
          checkInTime: zoomJoinTime,
          zoomJoinTime,
          lateMinutes: lateMinutes > 0 ? lateMinutes : null,
          autoMarked,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Error marking attendance with Zoom data:', error);
      throw error;
    }
  }

  private async updateAttendanceZoomLeaveTime(
    teacherId: string,
    studentId: string,
    sessionId: string,
    leaveTime: Date
  ): Promise<void> {
    try {
      const attendanceRef = tenantDocument(
        teacherId,
        'attendance',
        `${sessionId}_${studentId}`
      );

      // حساب مدة الحضور
      const attendanceDoc = await getDoc(attendanceRef);
      if (attendanceDoc.exists()) {
        const data = attendanceDoc.data();
        const joinTime = data.zoomJoinTime?.toDate();
        const duration = joinTime
          ? Math.floor((leaveTime.getTime() - joinTime.getTime()) / (1000 * 60))
          : 0;

        await updateDoc(attendanceRef, {
          zoomLeaveTime: leaveTime,
          zoomDuration: duration,
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error('Error updating Zoom leave time:', error);
      throw error;
    }
  }

  // إرسال التذكيرات
  async sendSessionReminder(sessionId: string): Promise<void> {
    try {
      const session = await this.getSession(sessionId);
      if (!session) {
        throw new Error('Session not found');
      }

      const settings = await this.getTeacherAttendanceSettings(
        session.teacherId
      );
      if (!settings?.reminderSettings.enabled) {
        return;
      }

      // إرسال التذكيرات للطلاب
      const batch = writeBatch(db);

      for (const studentId of session.studentsIds) {
        // إنشاء إشعار للطالب
        const notificationRef = doc(collection(db, 'notifications'));
        batch.set(notificationRef, {
          recipientId: studentId,
          recipientType: 'student',
          type: 'session_reminder',
          title: 'تذكير بموعد الحصة',
          message: `سيبدأ درس ${
            session.title
          } في ${session.date.toLocaleTimeString('ar-SA')}`,
          sessionId: session.id,
          zoomMeetingId: session.zoomMeetingId,
          read: false,
          createdAt: serverTimestamp(),
        });

        // إرسال للآباء إذا كان مفعلاً
        if (settings.reminderSettings.includeParents) {
          // البحث عن الآباء وإرسال الإشعارات لهم
        }
      }

      await batch.commit();

      // تحديث حالة إرسال التذكير
      const tenantSessionRef = tenantDocument(
        session.teacherId,
        'sessions',
        sessionId
      );
      const tenantSessionSnapshot = await getDoc(tenantSessionRef);
      if (!tenantSessionSnapshot.exists()) {
        throw new Error('Session not found in tenant scope');
      }
      await updateDoc(tenantSessionRef, {
        reminderSent: true,
        reminderSentAt: serverTimestamp(),
      });

      console.log(`Session reminder sent for: ${session.title}`);
    } catch (error) {
      console.error('Error sending session reminder:', error);
      throw error;
    }
  }

  // تقارير الحضور المتقدمة
  async getAttendanceReport(
    teacherId: string,
    startDate: Date,
    endDate: Date,
    studentId?: string
  ): Promise<AttendanceRecord[]> {
    try {
      let tenantAttendanceQuery = query(
        tenantCollection(teacherId, 'attendance'),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc')
      );

      if (studentId) {
        tenantAttendanceQuery = query(
          tenantAttendanceQuery,
          where('studentId', '==', studentId)
        );
      }

      const tenantSnapshot = await getDocs(tenantAttendanceQuery);
      const mapRecord = (docSnapshot: any) => {
        const data = docSnapshot.data();
        return {
          id: docSnapshot.id,
          ...data,
          date: data.date?.toDate(),
          checkInTime: data.checkInTime?.toDate(),
          zoomJoinTime: data.zoomJoinTime?.toDate(),
          zoomLeaveTime: data.zoomLeaveTime?.toDate(),
        } as AttendanceRecord;
      };
      const records = tenantSnapshot.docs.map(mapRecord);

      // تصفية السجلات للمعلم فقط
      const teacherSessions = await this.getTeacherSessions(
        teacherId,
        startDate,
        endDate
      );
      const teacherSessionIds = new Set(teacherSessions.map(s => s.id));

      return records.filter(record => teacherSessionIds.has(record.sessionId));
    } catch (error) {
      console.error('Error getting attendance report:', error);
      return [];
    }
  }

  async getAttendanceStats(
    teacherId: string,
    startDate: Date,
    endDate: Date
  ): Promise<{
    totalSessions: number;
    totalStudents: number;
    averageAttendance: number;
    onTimeRate: number;
    autoMarkedRate: number;
  }> {
    try {
      const sessions = await this.getTeacherSessions(
        teacherId,
        startDate,
        endDate
      );
      const attendanceRecords = await this.getAttendanceReport(
        teacherId,
        startDate,
        endDate
      );

      const totalSessions = sessions.length;
      const totalStudents = new Set(attendanceRecords.map(r => r.studentId))
        .size;

      const presentRecords = attendanceRecords.filter(
        r => r.status === 'present' || r.status === 'late'
      );
      const onTimeRecords = attendanceRecords.filter(
        r => r.status === 'present'
      );
      const autoMarkedRecords = attendanceRecords.filter(r => r.autoMarked);

      const averageAttendance =
        attendanceRecords.length > 0
          ? (presentRecords.length / attendanceRecords.length) * 100
          : 0;

      const onTimeRate =
        attendanceRecords.length > 0
          ? (onTimeRecords.length / attendanceRecords.length) * 100
          : 0;

      const autoMarkedRate =
        attendanceRecords.length > 0
          ? (autoMarkedRecords.length / attendanceRecords.length) * 100
          : 0;

      return {
        totalSessions,
        totalStudents,
        averageAttendance: Math.round(averageAttendance * 100) / 100,
        onTimeRate: Math.round(onTimeRate * 100) / 100,
        autoMarkedRate: Math.round(autoMarkedRate * 100) / 100,
      };
    } catch (error) {
      console.error('Error getting attendance stats:', error);
      return {
        totalSessions: 0,
        totalStudents: 0,
        averageAttendance: 0,
        onTimeRate: 0,
        autoMarkedRate: 0,
      };
    }
  }
}

export const advancedAttendanceService = new AdvancedAttendanceService();
